import React, { useLayoutEffect, useState } from "react";
import * as SDCCore from "scandit-web-datacapture-core";
import { Feedback, ContextStatus, DataCaptureContext } from "scandit-web-datacapture-core";
import * as SDCBarcode from "scandit-web-datacapture-barcode";
import { BarcodeCaptureFeedback } from "scandit-web-datacapture-barcode";
import { logger } from "../../common/scripts/logger/log";

interface Props {
  onScan: (scanResult: string) => void;
}

class NonSuccessScanditStatusError extends Error {
  public readonly code: number;
  public readonly statusMessage: string;

  constructor(code: number, statusMessage: string) {
    const name = "NonSuccessScanditStatusError";
    super(`${name} Code: (${code}). Message: ${statusMessage}`);
    this.name = name;
    this.code = code;
    this.statusMessage = statusMessage;
  }
}

const engineLocation =
  "https://cdn.jsdelivr.net/npm/scandit-web-datacapture-barcode@6.x/build/engine/";

const Scanner: React.FC<Props> = ({ onScan }) => {
  const [scannerError, setScannerError] = useState<string | null>(null);

  useLayoutEffect(() => {
    async function runScanner() {
      const connectingElement = document.getElementById("data-capture-view");
      if (!connectingElement) {
        throw new Error("Scanner view container is missing");
      }

      await SDCCore.configure({
        licenseKey: String(process?.env?.REACT_APP_SCANDIT_LICENSE_KEY),
        libraryLocation: engineLocation,
        moduleLoaders: [SDCBarcode.barcodeCaptureLoader()],
      });

      const context = await SDCCore.DataCaptureContext.create();

      context.addListener({
        didChangeStatus: (_context: DataCaptureContext, status: ContextStatus) => {
          const { code } = status;
          // https://docs.scandit.com/data-capture-sdk/web/core/api/context-status.html
          if (code !== 1) {
            const error = new NonSuccessScanditStatusError(code, status.message);
            logger.error(error.message, { ...error });
          }
        },
      });

      const camera = SDCCore.Camera.default;
      await context.setFrameSource(camera);

      const settings = new SDCBarcode.BarcodeCaptureSettings();
      settings.enableSymbologies([SDCBarcode.Symbology.DataMatrix]);
      settings.codeDuplicateFilter = 5000;
      settings.settingsForSymbology(SDCBarcode.Symbology.Code39);

      const barcodeCapture = await SDCBarcode.BarcodeCapture.forContext(context, settings);
      const barcodeCaptureFeedback = new BarcodeCaptureFeedback();
      const vibration = null;
      const sound = null;
      barcodeCaptureFeedback.success = new Feedback(vibration, sound);
      await barcodeCapture.setFeedback(barcodeCaptureFeedback);
      await barcodeCapture.setEnabled(false);

      barcodeCapture.addListener({
        didScan: async (barcodeCapture, session) => {
          const barcode = session.newlyRecognizedBarcodes[0];
          if (barcode.data) {
            onScan(barcode.data);
          }
          await barcodeCapture.setEnabled(true);
        },
      });

      const view = await SDCCore.DataCaptureView.forContext(context);
      view.connectToElement(connectingElement);
      view.addControl(new SDCCore.CameraSwitchControl());

      const barcodeCaptureOverlay =
        await SDCBarcode.BarcodeCaptureOverlay.withBarcodeCaptureForViewWithStyle(
          barcodeCapture,
          view,
          SDCBarcode.BarcodeCaptureOverlayStyle.Frame,
        );

      const viewfinder = new SDCCore.RectangularViewfinder(
        SDCCore.RectangularViewfinderStyle.Square,
        SDCCore.RectangularViewfinderLineStyle.Light,
      );

      await barcodeCaptureOverlay.setViewfinder(viewfinder);

      await camera.switchToDesiredState(SDCCore.FrameSourceState.On);
      await barcodeCapture.setEnabled(true);
    }

    runScanner().catch(error => {
      setScannerError(error?.message ?? "Scanner unavailable");
      logger.error("Scanner error", { error });
    });
  }, [onScan]);

  return (
    <>
      <div key="data-capture-view" id="data-capture-view" />
      {scannerError ? <div className="scanner-error">{scannerError}</div> : null}
    </>
  );
};

export default Scanner;
